import { useState, type FormEvent, type JSX } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Copy, KeyRound, Lock, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import type { ApiKeyDTO, ApiKeyWithSecretDTO } from '@caratuva/shared-types';
import { createApiKey, getProductionAccessStatus, listApiKeys, revokeApiKey } from '../lib/api';

export const Route = createFileRoute('/_app/developers/api-keys')({
  component: ApiKeysPage,
});

function ApiKeysPage(): JSX.Element {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<ApiKeyWithSecretDTO | null>(null);

  const query = useQuery({
    queryKey: ['api-keys'],
    queryFn: ({ signal }) => listApiKeys(signal),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success(
        intl.formatMessage({
          id: 'developers.apiKeys.revoked',
          defaultMessage: 'API key revoked.',
        }),
      );
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  function onRevoke(key: ApiKeyDTO): void {
    if (key.revokedAt) return;
    const confirmed = window.confirm(
      intl.formatMessage(
        {
          id: 'developers.apiKeys.confirmRevoke',
          defaultMessage:
            'Revoke {prefix}? This is irreversible and any integration using this key will stop working.',
        },
        { prefix: key.keyPrefix },
      ),
    );
    if (!confirmed) return;
    revoke.mutate(key.id);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'developers.apiKeys.description',
            defaultMessage:
              'Authenticate API requests with a Bearer token in the X-API-Key header. Test keys (pk_test_) target sandbox flows; live keys (pk_live_) operate on production data.',
          })}
        </p>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
          {intl.formatMessage({
            id: 'developers.apiKeys.create',
            defaultMessage: 'Create API key',
          })}
        </Button>
      </div>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'developers.apiKeys.loading',
            defaultMessage: 'Loading API keys...',
          })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'developers.apiKeys.error',
            defaultMessage: 'Failed to load API keys.',
          })}
        </p>
      ) : query.data.length === 0 ? (
        <EmptyState
          message={intl.formatMessage({
            id: 'developers.apiKeys.empty',
            defaultMessage: 'No API keys yet. Create one to start integrating.',
          })}
        />
      ) : (
        <div className="overflow-hidden rounded-md border border-navy-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.name',
                    defaultMessage: 'Name',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.key',
                    defaultMessage: 'Key',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.mode',
                    defaultMessage: 'Mode',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.lastUsed',
                    defaultMessage: 'Last used',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.created',
                    defaultMessage: 'Created',
                  })}
                </TableHead>
                <TableHead>
                  {intl.formatMessage({
                    id: 'developers.apiKeys.column.status',
                    defaultMessage: 'Status',
                  })}
                </TableHead>
                <TableHead className="w-[1%]"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium text-navy-900">{key.name}</TableCell>
                  <TableCell className="font-mono text-xs text-navy-700">
                    {key.keyPrefix}…
                  </TableCell>
                  <TableCell>
                    <Badge variant={key.testMode ? 'secondary' : 'default'}>
                      {key.testMode ? 'test' : 'live'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-navy-600 tabular-nums">
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell className="text-navy-600 tabular-nums">
                    {new Date(key.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {key.revokedAt ? (
                      <Badge variant="destructive">
                        {intl.formatMessage({
                          id: 'developers.apiKeys.status.revoked',
                          defaultMessage: 'Revoked',
                        })}
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        {intl.formatMessage({
                          id: 'developers.apiKeys.status.active',
                          defaultMessage: 'Active',
                        })}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {key.revokedAt ? null : (
                      <button
                        type="button"
                        onClick={() => onRevoke(key)}
                        disabled={revoke.isPending}
                        className="rounded p-1.5 text-navy-600 hover:bg-navy-50 hover:text-danger disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label={intl.formatMessage({
                          id: 'developers.apiKeys.revoke',
                          defaultMessage: 'Revoke',
                        })}
                      >
                        <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateApiKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          setCreateOpen(false);
          setRevealed(created);
          void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
        }}
      />

      <RevealSecretDialog secret={revealed} onClose={() => setRevealed(null)} />
    </div>
  );
}

function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-navy-200 bg-white px-6 py-10 text-center">
      <KeyRound aria-hidden className="h-6 w-6 text-navy-500" strokeWidth={1.5} />
      <p className="text-sm text-navy-600">{message}</p>
    </div>
  );
}

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (key: ApiKeyWithSecretDTO) => void;
}

function CreateApiKeyDialog({ open, onOpenChange, onCreated }: CreateDialogProps): JSX.Element {
  const intl = useIntl();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');

  // Live (pk_live_) keys require production approval — the same gate the mode
  // switcher enforces (#77). Without it the server rejects creation with 400
  // ProductionAccessNotApproved, so we lock the Live option up front instead of
  // surfacing a server error. Shares the query key with the header switcher so
  // the status is fetched once and cached.
  const access = useQuery({
    queryKey: ['production-access-status'],
    queryFn: ({ signal }) => getProductionAccessStatus(signal),
  });
  const liveApproved = access.data?.liveAccessApproved ?? false;

  const create = useMutation({
    mutationFn: () => createApiKey({ name: name.trim(), mode }),
    onSuccess: (created) => {
      setName('');
      setMode('test');
      onCreated(created);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  function selectMode(next: 'test' | 'live'): void {
    if (next === 'live' && !liveApproved) return;
    setMode(next);
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!name.trim() || create.isPending) return;
    if (mode === 'live' && !liveApproved) return;
    create.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: 'developers.apiKeys.dialog.title',
              defaultMessage: 'Create API key',
            })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({
              id: 'developers.apiKeys.dialog.description',
              defaultMessage:
                'You will see the secret only once. Store it in your secret manager immediately.',
            })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="api-key-name">
              {intl.formatMessage({
                id: 'developers.apiKeys.dialog.name',
                defaultMessage: 'Name',
              })}
            </Label>
            <Input
              id="api-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'developers.apiKeys.dialog.namePlaceholder',
                defaultMessage: 'CI pipeline',
              })}
              maxLength={80}
              autoFocus
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              {intl.formatMessage({
                id: 'developers.apiKeys.dialog.mode',
                defaultMessage: 'Mode',
              })}
            </Label>
            <div className="flex gap-2">
              <ModeOption
                value="test"
                current={mode}
                onSelect={selectMode}
                label="Test (pk_test_)"
              />
              <ModeOption
                value="live"
                current={mode}
                onSelect={selectMode}
                label="Live (pk_live_)"
                locked={!liveApproved}
                title={
                  liveApproved
                    ? undefined
                    : intl.formatMessage({
                        id: 'mode.live.needsApproval',
                        defaultMessage:
                          'Request production access in Onboarding to unlock live mode',
                      })
                }
              />
            </div>
            {liveApproved ? null : (
              <p className="text-xs text-navy-500">
                {intl.formatMessage({
                  id: 'developers.apiKeys.dialog.liveLocked',
                  defaultMessage:
                    'Live keys unlock after production is enabled for your organization.',
                })}{' '}
                <Link
                  to="/onboarding"
                  hash="production-access"
                  onClick={() => onOpenChange(false)}
                  className="font-medium text-navy-700 underline underline-offset-2 hover:text-navy-900"
                >
                  {intl.formatMessage({ id: 'goLive.title', defaultMessage: 'Enable production' })}
                </Link>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || !name.trim() || (mode === 'live' && !liveApproved)}
            >
              {create.isPending
                ? intl.formatMessage({
                    id: 'developers.apiKeys.dialog.submitting',
                    defaultMessage: 'Creating...',
                  })
                : intl.formatMessage({
                    id: 'developers.apiKeys.dialog.submit',
                    defaultMessage: 'Create',
                  })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ModeOptionProps {
  value: 'test' | 'live';
  current: 'test' | 'live';
  onSelect: (v: 'test' | 'live') => void;
  label: string;
  locked?: boolean;
  title?: string | undefined;
}

function ModeOption({
  value,
  current,
  onSelect,
  label,
  locked = false,
  title,
}: ModeOptionProps): JSX.Element {
  const selected = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      disabled={locked}
      title={title}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? 'border-navy-700 bg-navy-50 text-navy-900'
          : 'border-navy-200 bg-white text-navy-700 hover:bg-navy-50'
      }`}
      aria-pressed={selected}
    >
      {label}
      {locked ? <Lock aria-hidden className="h-3.5 w-3.5" strokeWidth={1.5} /> : null}
    </button>
  );
}

interface RevealSecretDialogProps {
  secret: ApiKeyWithSecretDTO | null;
  onClose: () => void;
}

function RevealSecretDialog({ secret, onClose }: RevealSecretDialogProps): JSX.Element {
  const intl = useIntl();

  function copy(): void {
    if (!secret) return;
    void navigator.clipboard.writeText(secret.secret);
    toast.success(
      intl.formatMessage({
        id: 'developers.apiKeys.copied',
        defaultMessage: 'Copied to clipboard.',
      }),
    );
  }

  return (
    <Dialog open={secret !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: 'developers.apiKeys.reveal.title',
              defaultMessage: 'Save this API key',
            })}
          </DialogTitle>
          <DialogDescription>
            {intl.formatMessage({
              id: 'developers.apiKeys.reveal.description',
              defaultMessage:
                'This is the only time the full secret will be shown. Copy it now and store it securely.',
            })}
          </DialogDescription>
        </DialogHeader>
        {secret ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-navy-200 bg-navy-50 px-3 py-2">
              <code className="flex-1 break-all font-mono text-xs text-navy-900">
                {secret.secret}
              </code>
              <button
                type="button"
                onClick={copy}
                className="rounded p-1.5 text-navy-600 hover:bg-white hover:text-navy-900"
                aria-label={intl.formatMessage({
                  id: 'developers.apiKeys.reveal.copy',
                  defaultMessage: 'Copy',
                })}
              >
                <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {intl.formatMessage({
              id: 'developers.apiKeys.reveal.done',
              defaultMessage: "I've saved it",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
